import { ref } from 'vue'
import auth, { AuthProvider, getProvider, LoginProvider, setProvider } from './auth'
import {
  rolesHierarchy,
  decodeToken,
  findHighestRole,
  determineUserRole,
} from './utils/userRole'

import type { User } from './types/user'

const setup = (
  provider: LoginProvider,
  customProvider?: typeof auth,
) => {
  if (!customProvider) {
    setProvider(provider)
  }

  const user = ref<User | null>(null)
  const userRole = ref<string | null>(null)
  const highestUserRole = ref<string | null>(null)
  const token = ref<string | null>(null)

  const providerInstance = ref(customProvider || auth)

  const instance = () => providerInstance.value

  const logout = (logoutUrl: string = window.location.origin) => {
    instance().logout(logoutUrl)
  }

  const login = (namespaceId?: string, options?: any) => {
    instance().authorize(namespaceId, options)
  }

  const setToken = () => {
    token.value = instance().token.value
  }

  const setUser = () => {
    const currentToken = instance().token.value

    user.value = instance().user.get()
    if (currentToken) {
      const decodedToken = decodeToken(currentToken)
      highestUserRole.value = findHighestRole(decodedToken)
      userRole.value = determineUserRole(highestUserRole.value)
      setUserRoleAttrToBody(userRole.value)
    }
  }

  const setUserRoleAttrToBody = (role: string) => {
    document.body.dataset.userRole = role
  }

  return {
    user,
    token,
    userRole,
    highestUserRole,
    setUser,
    setToken,
    login,
    logout,
    instance,
    setUserRoleAttrToBody,
    rolesHierarchy,
    providerInstance,
  }
}

let instance: ReturnType<typeof setup> | undefined = undefined

export default (
  provider: LoginProvider = AuthProvider.EPORTAL,
  customProvider?: typeof auth,
): ReturnType<typeof setup> => {
  if (instance === undefined || getProvider() !== provider) {
    instance = setup(provider, customProvider)
  }

  return instance
}
